import { applyRepeatFieldDependencies, isJson } from "Utils/Common/Requisition";

const isRepeatField = (field: any) =>
  field?.uiType === "Repeat" || field?.uiTypeId === 33;

/** Lookup fields that cannot be selected twice across repeat blocks. */
export const EXCLUSIVE_LOOKUP_FIELDS = new Set([
  "Providers",
  "SalesRepUser",
]);

export const isExclusiveLookupField = (systemFieldName?: string) =>
  Boolean(systemFieldName && EXCLUSIVE_LOOKUP_FIELDS.has(systemFieldName));

/** Extract selected lookup value(s) from a field defaultValue. */
export const extractLookupValues = (defaultValue: any): string[] => {
  if (defaultValue == null || defaultValue === "") return [];

  if (typeof defaultValue === "string") {
    if (isJson(defaultValue)) {
      try {
        const parsed = JSON.parse(defaultValue);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => String(item?.value ?? item?.id ?? item ?? ""))
            .filter((value) => value !== "");
        }
        const single = String(parsed?.value ?? parsed?.id ?? parsed ?? "");
        return single ? [single] : [];
      } catch {
        return [defaultValue];
      }
    }
    return [defaultValue];
  }

  if (Array.isArray(defaultValue)) {
    return defaultValue
      .map((item) => String(item?.value ?? item?.id ?? item ?? ""))
      .filter((value) => value !== "");
  }

  return [String(defaultValue)].filter((value) => value !== "");
};

const collectSelectedValuesFromRepeatBlock = (
  repeatBlock: any,
  systemFieldName: string
): string[] => {
  const values: string[] = [];

  repeatBlock?.repeatFields?.forEach((field: any) => {
    if (field?.systemFieldName === systemFieldName) {
      values.push(...extractLookupValues(field?.defaultValue));
    }
  });

  repeatBlock?.repeatDependencyControls?.forEach((control: any) => {
    control?.dependecyFields?.forEach((depField: any) => {
      if (depField?.systemFieldName === systemFieldName) {
        values.push(...extractLookupValues(depField?.defaultValue));
      }
    });
  });

  return values;
};

/**
 * Filter dropdown/list options so values already selected in other repeat
 * blocks are hidden. The current block keeps its own selected value(s).
 * Removing a block automatically restores its value to sibling lookups.
 */
export const getExclusiveLookupOptions = (
  section: any,
  currentRepeatFieldIndex: number,
  systemFieldName: string,
  options: any[] = []
) => {
  if (!isExclusiveLookupField(systemFieldName) || !Array.isArray(options)) {
    return options;
  }

  const usedElsewhere = new Set<string>();

  section?.fields?.forEach((field: any, fieldIndex: number) => {
    if (fieldIndex === currentRepeatFieldIndex) return;
    if (!isRepeatField(field)) return;

    collectSelectedValuesFromRepeatBlock(field, systemFieldName).forEach(
      (value) => usedElsewhere.add(value)
    );
  });

  if (usedElsewhere.size === 0) return options;

  return options.filter((option) => {
    const value = String(option?.value ?? option?.id ?? "");
    return value === "" || !usedElsewhere.has(value);
  });
};

/** Restore repeat block definitions from API snapshot arrays on the repeat field. */
const resetRepeatFieldFromState = (repeatField: any) => {
  if (!repeatField) return repeatField;

  if (repeatField.repeatFieldsState?.length) {
    repeatField.repeatFields = JSON.parse(
      JSON.stringify(repeatField.repeatFieldsState)
    );
  }
  if (repeatField.repeatDependencyControlsState?.length) {
    repeatField.repeatDependencyControls = JSON.parse(
      JSON.stringify(repeatField.repeatDependencyControlsState)
    );
  }

  return repeatField;
};

/** Remove edit-mode lock class so newly added blocks stay editable. */
const clearOverlayClass = (field: any) => {
  if (typeof field?.displayType !== "string") return;
  field.displayType = field.displayType
    .replace(/\s*overlay\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

/** Clone a new provider repeat block from API state snapshots, clearing saved values. */
export const createFacilityRepeatBlockFromTemplate = (repeatField: any) => {
  const block = JSON.parse(JSON.stringify(repeatField ?? {}));
  resetRepeatFieldFromState(block);

  block.repeatFields?.forEach((field: any) => {
    field.defaultValue = "";
    field.signatureText = "";
    field.selectedText = "";
    clearOverlayClass(field);
  });

  block.repeatDependencyControls?.forEach((control: any) => {
    control.dependecyFields?.forEach((depField: any) => {
      depField.defaultValue = "";
      depField.signatureText = "";
      depField.selectedText = "";
      clearOverlayClass(depField);
    });
  });

  return block;
};

/** Apply repeat dependencies from isSelectedDefault / defaultValue — dynamic-facility only. */
export const applyFacilityRepeatDependenciesOnLoad = (inputs: any) => {
  let result = inputs;
  result?.forEach((section: any, sectionIndex: number) => {
    section?.fields?.forEach((field: any, fieldIndex: number) => {
      if (isRepeatField(field)) {
        result = applyRepeatFieldDependencies(result, sectionIndex, fieldIndex);
      }
    });
  });
  return result;
};
